import React, { useState } from 'react';
import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

/**
 * EntityCell - the universal "first column" of every listings table.
 *
 * Purpose: at a single glance the user knows *what* a row is and
 * *what state* it's in, before even reading the title. The shape is
 * identical on every page so scanning a long list feels uniform:
 *
 *     ║  a3f9e1d2          ← short id (monospace, click to copy)
 *     ║  Completed         ← state label, in tone colour
 *
 * The leading ║ is a tone-coloured stripe full row height. Linear /
 * Vercel / Plane all use this pattern - it draws the eye down the
 * status column without needing chips on every row.
 *
 * Props:
 *   id      - the full identifier (UUID); displayed as the trailing
 *             8 chars in monospace, click-to-copy
 *   tone    - 'success' | 'error' | 'warning' | 'info' | 'primary' | 'neutral'
 *   label   - state label rendered below the ID (e.g. "Completed")
 *   sublabel- optional secondary line (e.g. duration / time delta)
 *   prefix  - optional prefix shown before the trailing chars (e.g. "t/")
 *   length  - default 8, how many trailing characters of ``id`` to show
 *   compact - smaller font / tighter spacing (default false)
 */

const TONE_COLOR = (theme, tone) => {
  switch (tone) {
    case 'success': return theme.palette.success.main;
    case 'error':   return theme.palette.error.main;
    case 'warning': return theme.palette.warning.main;
    case 'info':    return theme.palette.info.main;
    case 'primary': return theme.palette.primary.main;
    case 'neutral':
    default:        return theme.palette.text.disabled;
  }
};

const EntityCell = ({
  id,
  tone = 'neutral',
  label,
  sublabel,
  prefix = '',
  length = 8,
  compact = false,
}) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const color = TONE_COLOR(theme, tone);

  const stripped = String(id || '').replace(/-/g, '');
  const tail = stripped.slice(Math.max(0, stripped.length - length));

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!id) return;
    try {
      await navigator.clipboard.writeText(String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1100);
    } catch (_e) {
      // clipboard unavailable in non-secure contexts; ignore.
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'stretch',
      // Stretch the bar to the full cell height by negating the cell's
      // vertical padding. The DataTable cell uses 10px vertical padding
      // so a -10px margin-Y gives a flush-edge stripe.
      my: '-10px',
      py: '10px',
      ml: compact ? 0 : '-4px',
      gap: compact ? 0.875 : 1.25,
    }}>
      {/* Tone stripe */}
      <Box sx={{
        width: 3,
        flexShrink: 0,
        borderRadius: 0.5,
        bgcolor: color,
      }} />

      {/* Identity stack */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
        {/* Short ID - monospace, copy on click */}
        <Tooltip title={copied ? 'Copied!' : (id ? `${id} — click to copy` : '')} placement="top" arrow>
          <Box
            component="span"
            onClick={handleCopy}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
              fontSize: compact ? 11 : 12,
              fontWeight: 500,
              lineHeight: 1.25,
              color: theme.palette.text.primary,
              cursor: 'pointer',
              transition: 'color 100ms ease',
              '&:hover': {
                color: color,
              },
              maxWidth: '100%',
              whiteSpace: 'nowrap',
            }}
          >
            {prefix}
            {tail || '—'}
            {copied && (
              <CheckIcon sx={{
                fontSize: 11,
                color: theme.palette.success.main,
                ml: 0.25,
              }} />
            )}
          </Box>
        </Tooltip>

        {/* State label - tone color, small */}
        {label && (
          <Typography
            component="span"
            sx={{
              fontSize: compact ? 10 : 11,
              fontWeight: 600,
              lineHeight: 1.3,
              color: color,
              letterSpacing: 0.2,
              textTransform: 'capitalize',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {label}
          </Typography>
        )}

        {/* Optional sublabel - muted */}
        {sublabel && (
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              fontWeight: 400,
              lineHeight: 1.3,
              color: theme.palette.text.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EntityCell;
